import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

export const Question = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 1px;
  padding-right: 1px;
  position: relative;
  overflow: hidden; /* Change from hidden to visible */
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
`;

export const MailanderUserLogo = styled.img`
  width: 28px;
  height: 28px;
`;

export const LogoAndListenWrapper = styled.div`
  height: 36px;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
`;

export const ProgressBarWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  position: relative;
  cursor: pointer;
  user-select: none;
  touch-action: none;
  height: 100%;

  &::* {
    pointer-events: none;
    hover {
      pointer-events: all;
    }
  }
`;

export const ProgressTrack = styled.div<{ progress: number }>`
  height: 3px;
  background: #2b2d33;
  border-radius: 9999px;
  width: ${(p) => 100 - p.progress}%;

  &::* {
    pointer-events: none;
  }
`;

export const ProgressBar = styled.div<{ progress: number }>`
  width: ${(p) => p.progress}%;
  height: 3px;
  background: #5e84ed;
  border-radius: 9999px;

  &::* {
    pointer-events: none;
  }
`;

export const ProgressText = styled.span`
  color: #b7bdcb;
  font-size: 14px;
  font-family: Inter;
  font-weight: 500;
  line-height: 19.6px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const listenPillPulse = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 0.85; }
`;

export const ListenPill = styled.div<{ isCollapsing?: boolean; isLoading?: boolean }>`
  padding: ${(p) => (p.isCollapsing ? '0px' : '6px 16px')};
  background: #5e84ed;
  border-radius: 999px;
  outline: 1px #5e84ed solid;
  outline-offset: -1px;
  justify-content: center;
  align-items: center;
  gap: ${(p) => (p.isCollapsing ? '0px' : '8px')};
  display: inline-flex;
  width: ${(p) => (p.isCollapsing ? '0px' : 'auto')};
  overflow: hidden;
  white-space: nowrap;
  cursor: ${(p) => (p.isLoading ? 'not-allowed' : 'pointer')};
  pointer-events: ${(p) => (p.isCollapsing || p.isLoading ? 'none' : 'auto')};
  color: #ffffff;
  transition: width 0.3s cubic-bezier(0.4, 0.0, 0.2, 1),
              padding 0.3s cubic-bezier(0.4, 0.0, 0.2, 1),
              gap 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
  ${(p) => p.isLoading && `animation: ${listenPillPulse} 1.4s ease-in-out infinite;`}
`;

export const ListenPillText = styled.span<{ isCollapsing?: boolean }>`
  color: white;
  font-size: 14px;
  font-family: Inter;
  font-weight: 500;
  line-height: 19.6px;
  word-wrap: break-word;
  opacity: ${(p) => (p.isCollapsing ? 0 : 1)};
  transition: opacity 0.15s ease;
`;

export const ListenPillIcon = styled.img<{ isCollapsing?: boolean }>`
  opacity: ${(p) => (p.isCollapsing ? 0 : 1)};
  transition: opacity 0.15s ease;
`;

export const AudioControlButton = styled.img`
  cursor: pointer;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
`;

export const PlayedWrapper = styled.div<{ isAnimating?: boolean }>`
  flex: ${(p) => (p.isAnimating ? '0 0 0px' : '1')};
  min-width: 0;
  overflow: hidden;
  background: #1e1f22;
  border-radius: 12px;
  outline: 1px #5e84ed solid;
  margin-right: 5px;
  height: 36px;
  width: ${(p) => (p.isAnimating ? '0px' : 'auto')};
  opacity: ${(p) => (p.isAnimating ? 0 : 1)};
  pointer-events: ${(p) => (p.isAnimating ? 'none' : 'auto')};
  transition: width 0.3s ease, flex 0.3s ease, opacity 0.3s ease;
`;

export const PlayedAudio = styled.div`
  width: 100%;
  padding: 6px 12px;
  background: #23252a;
  border-radius: 999px;
  outline: 1px #2b2d33 solid;
  outline-offset: -1px;
  gap: 12px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  height: 100%;
`;

export const QuestionText = styled.p`
  margin: 0;
  padding: 0 5px;
  padding-left: 36px;
  font-family: "Custora", serif;
  font-size: 16px;
  color: #e9ecf5;
  padding-bottom: 56px;
  line-height: 150%;

  a {
    color: #5E84ED;
    text-decoration: underline;
    word-break: break-all;
  }
`;

export const TopHiddingSpot = styled.div<{ visible?: boolean; top?: number }>`
  display: flex;
  position: fixed;
  width: 100%;
  z-index: 10;
  background: linear-gradient(to bottom, #1a1a1c 0%, rgba(26, 26, 28, 0) 100%);
  text-align: center;
  justify-content: center;
  align-items: center;
  height: 25px;
  transform: translateY(${(p) => (p.visible ? '-1px' : '-10%')});
  opacity: ${(p) => (p.visible ? 1 : 0)};
  transition:
    transform 240ms cubic-bezier(0.2, 0.8, 0.2, 1),
    opacity 180ms ease;
  padding: 0;
  margin: 0;
  pointer-events: none;
`;

export const BottomHiddingSpot = styled.div`
  display: flex;
  position: fixed;
  width: 100%;
  bottom: 0px;
  background: linear-gradient(to bottom, rgba(26, 26, 28, 0) 0%, #1a1a1c 53.37%);
  text-align: center;
  justify-content: center;
  align-items: center;
  height: 188px;
  pointer-events: none;
`;

export const ConditionalQuestionWrapper = styled.div<{ disableScroll: boolean }>`
  // display: flex;
  // flex-direction: column;
  position: relative;
  // overflow-y: ${(props) => (props.disableScroll ? 'visible' : 'auto')};
  -webkit-overflow-scrolling: touch;
  /* Prevent scroll bubbling */
  // overscroll-behavior: contain;
  /* Explicitly set the container to be the only scrollable element */
  isolation: isolate;
  // margin-bottom: 100px;
  // padding-bottom: 27px;
`;
